import i18n from '@/utils/i18n';
import React from 'react';
import { Button } from './button';
import { CombinedActionSheet } from './combined-action-sheet';
import { ListItemSeparator } from './list-item';
import { PrinterSelectList } from './printer-select-list';
import { StyleSheet, Text, View } from 'react-native';
import { TagPrintingBluetoothPermissions } from './tag-printing-bluetooth-permissions';
import { TYPOGRAPHY } from '@/styles';
import { useBluetoothContext } from '@/contexts/bluetooth-context';

enum TagPrintConfirmationRoute {
  BluetoothPermissions = 'BLUETOOTH_PERMISSIONS',
  PrinterSelection = 'PRINTER_SELECTION',
}

interface Props {
  isInstantPrint: boolean;
  loadingTemplates: boolean;
  onClose: () => void;
  onPrint: () => Promise<void>;
  onPrinterSelect: () => void;
  setIsInstantPrint: React.Dispatch<React.SetStateAction<boolean>>;
  tagCount: number;
}

export const TagPrintConfirmation: React.FC<Props> = ({
  isInstantPrint,
  loadingTemplates,
  onClose,
  onPrint,
  onPrinterSelect,
  setIsInstantPrint,
  tagCount,
}) => {
  const { hasPermission, selectedDevice } = useBluetoothContext();

  const exitInstantPrintMode = () => {
    setIsInstantPrint(false);
    onClose();
  };

  return (
    <CombinedActionSheet
      initialRoute={
        hasPermission
          ? TagPrintConfirmationRoute.PrinterSelection
          : TagPrintConfirmationRoute.BluetoothPermissions
      }
      onClose={onClose}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case TagPrintConfirmationRoute.BluetoothPermissions:
            return (
              <TagPrintingBluetoothPermissions
                goBack={onClose}
                onPermissionsGranted={() =>
                  jumpTo(TagPrintConfirmationRoute.PrinterSelection)
                }
              />
            );
          case TagPrintConfirmationRoute.PrinterSelection:
            return (
              <>
                <View style={styles.header}>
                  <Text style={TYPOGRAPHY.navigationTitle}>
                    {i18n.t(
                      isInstantPrint ? 'instant_print.title' : 'select_printer',
                    )}
                  </Text>
                  <Text style={[TYPOGRAPHY.p, styles.messageText]}>
                    {i18n.t(
                      isInstantPrint
                        ? 'instant_print.message'
                        : 'select_printer.message',
                    )}
                  </Text>
                </View>
                <PrinterSelectList
                  containerStyle={styles.printerSelect}
                  ItemSeparatorComponent={() => (
                    <ListItemSeparator style={styles.printerListSeparator} />
                  )}
                  listItemStyle={styles.printerSelectListItem}
                  onSelect={onPrinterSelect}
                />
                {isInstantPrint ? (
                  <Button
                    onPress={exitInstantPrintMode}
                    trackingData={{
                      name: 'instant_print_exit_button',
                    }}
                    type="secondary"
                  >
                    {i18n.t('instant_print.exit')}
                  </Button>
                ) : (
                  <Button
                    disabled={!selectedDevice}
                    loading={loadingTemplates}
                    onPress={onPrint}
                    // Tracking event is tracked in onPrint
                    trackingData={null}
                  >
                    {tagCount > 1
                      ? i18n.t('print_tag_plural', { count: tagCount })
                      : i18n.t('print_tag')}
                  </Button>
                )}
              </>
            );
        }
      }}
      routes={{
        [TagPrintConfirmationRoute.BluetoothPermissions]: {
          previousKey: null,
          showCloseButton: false,
        },
        [TagPrintConfirmationRoute.PrinterSelection]: {
          previousKey: null,
          showCloseButton: false,
        },
      }}
    />
  );
};

const styles = StyleSheet.create({
  header: {
    gap: 4,
  },
  messageText: {
    lineHeight: 22,
  },
  printerListSeparator: {
    marginHorizontal: 0,
  },
  printerSelect: {
    marginVertical: 16,
  },
  printerSelectListItem: {
    paddingHorizontal: 0,
  },
});
